import { useState, useEffect, useCallback, useContext, Fragment } from 'react';
import { toast } from 'react-toastify';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Chip,
  Alert,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsIcon from '@mui/icons-material/Directions';

// Custom Components
import { Spinner, SearchBox, PropertyDrawer, BrowserTitle } from '../components';

// Utils
import messages from '../static/messages';
import { nullClearObject } from '../utils/cleaners';
import { AuthContext } from '../context/AuthProvider';

// Services
import propertiesServices from '../services/propertiesServices';

// Hooks
import useSearch from '../hooks/useSearch';
import usePagination from '../hooks/usePagination';
import useDrawer from '../hooks/useDrawer';

// Styles
import {
  table_header_with_search,
  table_container_base,
  check_for_deleted_row,
  actions_column,
} from '../static/styles';

import { button } from '../static/theme-styles';
import { stringifyAddress } from '../utils/formatter';

// External variables
const base_property = {
  typeName: '',
  address: '',
};

// Mobile Property Card Component
const MobilePropertyCard = ({ property, onEdit, onRemove, onRestore, page, rowsPerPage }) => {
  const isDeleted = !!property.deletedAt;
  const address = stringifyAddress(property, true);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2, 
        opacity: isDeleted ? 0.6 : 1,
        bgcolor: isDeleted ? '#fafafa' : '#fff',
        borderColor: isDeleted ? '#e0e0e0' : '#4caf50',
        borderWidth: isDeleted ? 1 : 2,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header with Type and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            {isDeleted && (
              <Chip 
                label="DELETED" 
                size="small" 
                color="error" 
                variant="outlined"
                sx={{ mb: 1, fontSize: '0.65rem', height: 20 }}
              />
            )}
            
            {/* Property Type */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HomeIcon sx={{ color: '#4caf50', fontSize: 18 }} />
              <Chip 
                label={property.type?.name || 'Not specified'} 
                size="small" 
                sx={{ 
                  bgcolor: '#e8f5e9', 
                  color: '#2e7d32',
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isDeleted ? (
              <IconButton 
                size="small" 
                onClick={() => onRestore(property.id)}
                sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}
              >
                <SettingsBackupRestoreIcon sx={{ fontSize: 18, color: '#4caf50' }} />
              </IconButton>
            ) : (
              <>
                <IconButton 
                  size="small" 
                  onClick={() => onEdit(property)}
                  sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                >
                  <EditIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => onRemove(property.id)}
                  sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
                >
                  <DeleteForeverIcon sx={{ fontSize: 18, color: '#f44336' }} />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {/* Address - Clickable to property page */}
        <NavLink
          to={`/properties/${property.id}`}
          onClick={isDeleted ? (e) => e.preventDefault() : null}
          state={{ page, rowsPerPage }}
          style={{ textDecoration: 'none' }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: 1, 
            p: 1.5,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            mb: 1.5,
            '&:hover': { bgcolor: isDeleted ? '#f5f5f5' : '#eeeeee' },
          }}>
            <LocationOnIcon sx={{ color: '#757575', fontSize: 20, mt: 0.3 }} />
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  color: isDeleted ? '#999' : '#333',
                  textDecoration: isDeleted ? 'line-through' : 'none',
                }}
              >
                {property.streetNumber} {property.streetName}
                {property.unit && ` #${property.unit}`}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {property.city}, {property.state} {property.zipCode}
              </Typography>
            </Box>
            {!isDeleted && <ChevronRightIcon sx={{ color: '#999', fontSize: 20, mt: 0.5 }} />}
          </Box>
        </NavLink>

        {/* Navigate Button */}
        {!isDeleted && (
          <Button
            component="a"
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            startIcon={<DirectionsIcon />}
            fullWidth
            sx={{ 
              color: '#4caf50', 
              borderColor: '#4caf50',
              '&:hover': { bgcolor: '#e8f5e9', borderColor: '#2e7d32' },
            }}
          >
            Navigate
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export const Properties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { userData } = useContext(AuthContext);

  const [propertiesList, setPropertiesList] = useState();
  const [totalCount, setTotalCount] = useState();
  const [showDeleted, setShowDeleted] = useState(userData.preferences.showDeletedRows);
  const [showInvalid, setShowInvalid] = useState(false);
  const [currentProperty, setCurrentProperty] = useState();
  const [isNewProperty, setPropertyMode] = useState(false);
  
  // Duplicate property detection states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateProperty, setDuplicateProperty] = useState(null);
  
  // Multi-unit building detection states
  const [showMultiUnitDialog, setShowMultiUnitDialog] = useState(false);
  const [existingUnits, setExistingUnits] = useState([]);

  // Similar address detection states
  const [showSimilarDialog, setShowSimilarDialog] = useState(false);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [pendingCreateData, setPendingCreateData] = useState(null);

  const { searchQuery, setSearchQuery, isSearching, searchRequest } = useSearch(propertiesServices.searchRequest);
  const { rowsPerPage, setRowsPerPage, page, setPage, handleChangePage, handleChangeRowsPerPage } = usePagination();
  const { drawer, openDrawer, foolCheck } = useDrawer(currentProperty);

  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  // Action functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();

    if (confirmed === true) {
      setCurrentProperty(null);
      setPropertyMode(false);
      resetErrors && resetErrors();
    }
  };

  const onEditProperty = (property) => {
    delete property.createdAt;
    delete property.deletedAt;
    delete property.updatedAt;
    const cleanObject = nullClearObject(property);
    setCurrentProperty(cleanObject);
    openDrawer();
  };

  const onAddProperty = () => {
    setPropertyMode(true);
    setCurrentProperty(base_property);
    openDrawer();
  };

  // Handler functions
  const handleRemove = (id) => {
    confirm({
      title: 'Delete Property Permanently?',
      description: 'This property will be permanently deleted. This action cannot be undone. Properties with proposals or appointments cannot be deleted.',
    })
      .then(() => removeProperty(id))
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleRestore = (id) => {
    confirm({
      title: messages.confirm_message.restore,
      description: messages.confirm_message.restore_description('Property'),
    })
      .then(() => restoreProperty(id))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const getPropertiesList = async (skip, take, widthDeleted = true) => {
    try {
      const response = await propertiesServices.getAllProperties(skip, take, widthDeleted);
      setTotalCount(response.data.total);
      setPropertiesList(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getInvalidProperties = async (skip, take) => {
    try {
      const response = await propertiesServices.getInvalidProperties(skip, take);
      setTotalCount(response.data.total);
      setPropertiesList(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getSearchResult = useCallback(
    async (skip, take) => {
      const result = await searchRequest(skip, take);
      setTotalCount(result.total);
      setPropertiesList(result.data);
    },
    [searchRequest],
  );

  const removeProperty = async (id) => {
    try {
      const response = await propertiesServices.remove(id);

      if (response.status === 200) {
        toast.success('Property deleted permanently');
        getPropertiesList(page * rowsPerPage, rowsPerPage, showDeleted);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(errorMessage);
      console.log(error);
    }
  };

  const restoreProperty = async (id) => {
    try {
      const response = await propertiesServices.restore(id);

      if (response.status === 200) {
        toast.success(messages.property.restore);
        getPropertiesList(page * rowsPerPage, rowsPerPage, showDeleted);
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const createProperty = async () => {
    delete currentProperty.isEdited;

    const data = {
      typeName: currentProperty.typeName?.name || currentProperty.typeName,
      streetNumber: currentProperty.streetNumber,
      streetName: currentProperty.streetName,
      unit: currentProperty.unit || '',
      city: currentProperty.city,
      state: currentProperty.state,
      zipCode: currentProperty.zipCode,
    };

    // Check for duplicate property BEFORE creating
    try {
      const duplicateCheck = await propertiesServices.checkDuplicate({
        streetNumber: data.streetNumber,
        streetName: data.streetName,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
        unit: data.unit,
      });
      
      console.log('Duplicate check:', duplicateCheck.data);
      
      if (duplicateCheck.data?.isDuplicate && duplicateCheck.data?.existingProperty) {
        setDuplicateProperty(duplicateCheck.data.existingProperty);
        setShowDuplicateDialog(true);
        return;
      }
      
      if (duplicateCheck.data?.isMultiUnitBuilding && duplicateCheck.data?.requiresUnit) {
        setExistingUnits(duplicateCheck.data.existingUnits || []);
        setShowMultiUnitDialog(true);
        return;
      }

      if (duplicateCheck.data?.similarProperties?.length > 0) {
        setSimilarProperties(duplicateCheck.data.similarProperties);
        setPendingCreateData(data);
        setShowSimilarDialog(true);
        return;
      }
    } catch (error) {
      console.log('Duplicate check error:', error);
      const msg = error?.response?.data?.message;
      if (msg) {
        toast.error(msg);
        return;
      }
    }

    try {
      const response = await propertiesServices.create(data);

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.property.create);
        navigate(`/properties/${response.data.id}`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };
  
  const handleGoToExistingProperty = () => {
    setShowDuplicateDialog(false);
    if (duplicateProperty?.id) {
      onCloseDrawer();
      navigate(`/properties/${duplicateProperty.id}`);
    }
  };

  const handleGoToSimilarProperty = (propertyId) => {
    setShowSimilarDialog(false);
    setSimilarProperties([]);
    setPendingCreateData(null);
    onCloseDrawer();
    navigate(`/properties/${propertyId}`);
  };

  const forceCreateProperty = async () => {
    setShowSimilarDialog(false);
    const data = pendingCreateData;
    setPendingCreateData(null);
    setSimilarProperties([]);
    if (!data) return;

    try {
      const response = await propertiesServices.create(data);
      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.property.create);
        navigate(`/properties/${response.data.id}`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };

  const updateProperty = async () => {
    delete currentProperty.isEdited;

    // Check for duplicate BEFORE updating so the user gets a clear dialog
    // with a link to the existing property instead of a generic 400 error
    try {
      const duplicateCheck = await propertiesServices.checkDuplicate({
        streetNumber: currentProperty.streetNumber,
        streetName: currentProperty.streetName,
        city: currentProperty.city,
        state: currentProperty.state,
        zipCode: currentProperty.zipCode,
        unit: currentProperty.unit || '',
      });

      const existing = duplicateCheck.data?.existingProperty;
      if (duplicateCheck.data?.isDuplicate && existing && existing.id !== currentProperty.id) {
        setDuplicateProperty(existing);
        setShowDuplicateDialog(true);
        return;
      }
    } catch (error) {
      console.log('Duplicate check error:', error);
    }

    try {
      const response = await propertiesServices.update(currentProperty.id, {
        typeName: currentProperty.typeName?.name || currentProperty.typeName,
        streetNumber: currentProperty.streetNumber,
        streetName: currentProperty.streetName,
        unit: currentProperty.unit || '',
        city: currentProperty.city,
        state: currentProperty.state,
        zipCode: currentProperty.zipCode,
        isCorrect: currentProperty.isCorrect,
      });

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.property.update);

        if (showInvalid) {
          getInvalidProperties(page * rowsPerPage, rowsPerPage);
        } else {
          getPropertiesList(page * rowsPerPage, rowsPerPage, showDeleted);
        }
      }
    } catch (error) {
      const msg = error?.response?.data?.message || messages.errors.error_try_again;
      toast.error(msg);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    searchQuery === ''
      ? showInvalid
        ? getInvalidProperties(page * rowsPerPage, rowsPerPage)
        : getPropertiesList(page * rowsPerPage, rowsPerPage, showDeleted)
      : getSearchResult(page * rowsPerPage, rowsPerPage, showDeleted);
  }, [searchQuery, getSearchResult, page, rowsPerPage, showDeleted]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setShowDeleted(userData.isDeletedRowsVisible);

    if (location.search) {
      const params = new URLSearchParams(location.search);
      const page = params.get('page');
      const rowsPerPage = params.get('per_page');

      setPage(Number(page));
      setRowsPerPage(Number(rowsPerPage));
    }
  }, []);

  useEffect(() => {
    setPage(0);
  }, [totalCount]);

  useEffect(() => {
    if (showInvalid) getInvalidProperties(page * rowsPerPage, rowsPerPage);
    else getPropertiesList(page * rowsPerPage, rowsPerPage, showDeleted);
  }, [showInvalid]);

  // Check for data loading
  if (!propertiesList) return <Spinner />;

  return (
    <Box component="section">
      <BrowserTitle title="Properties | Airmaxx Pro" />
      
      {/* Header - responsive */}
      <Box sx={{ 
        ...table_header_with_search,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        alignItems: isMobile ? 'stretch' : 'center',
      }}>
        <Button 
          variant="contained" 
          endIcon={<AddIcon />} 
          onClick={onAddProperty} 
          sx={{ 
            ...button('primary', 'secondary'),
            width: isMobile ? '100%' : 'auto',
          }}
        >
          Add New Property
        </Button>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'row' : 'row',
          justifyContent: isMobile ? 'space-between' : 'flex-start',
          gap: 1,
        }}>
          <FormControlLabel
            value="end"
            control={<Switch color="primary" size={isMobile ? 'small' : 'medium'} checked={showInvalid} onChange={() => setShowInvalid(!showInvalid)} />}
            label={<Typography variant={isMobile ? 'body2' : 'body1'}>Invalid</Typography>}
            labelPlacement="end"
            disabled={searchQuery !== ''}
          />

          {!showInvalid && (
            <FormControlLabel
              value="end"
              control={<Switch color="primary" size={isMobile ? 'small' : 'medium'} checked={showDeleted} onChange={() => setShowDeleted(!showDeleted)} />}
              label={<Typography variant={isMobile ? 'body2' : 'body1'}>Deleted</Typography>}
              labelPlacement="end"
              disabled={searchQuery !== ''}
            />
          )}
        </Box>
      </Box>

      {/* Search Box */}
      <Paper sx={{ mb: 2 }}>
        <SearchBox searchRequest={searchRequest} value={searchQuery} setValue={setSearchQuery} />
      </Paper>

      {/* Mobile Summary */}
      {isMobile && (
        <Box sx={{ mb: 2, px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {totalCount || 0} properties found
            {showInvalid && ' (showing invalid only)'}
          </Typography>
        </Box>
      )}

      {/* Desktop Table View */}
      {!isMobile && (
        <Paper>
          <TableContainer sx={table_container_base}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="15%">Type</TableCell>
                  <TableCell width="80%">Address</TableCell>
                  <TableCell width="5%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isSearching ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Spinner />
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment>
                    {propertiesList.length ? (
                      propertiesList.map((property) => (
                        <TableRow key={property.id} sx={check_for_deleted_row(!!property.deletedAt)} hover>
                          <TableCell scope="row">
                            <NavLink
                              className="full-col-flex-link"
                              to={`/properties/${property.id}`}
                              onClick={!!property.deletedAt ? (e) => e.preventDefault() : null}
                              state={{ page, rowsPerPage }}
                            >
                              {property.type?.name || 'Not specified'}
                            </NavLink>
                          </TableCell>
                          <TableCell>
                            <NavLink
                              className="full-col-flex-link"
                              to={`/properties/${property.id}`}
                              onClick={!!property.deletedAt ? (e) => e.preventDefault() : null}
                              state={{ page, rowsPerPage }}
                            >
                              {stringifyAddress(property, true)}
                            </NavLink>
                          </TableCell>
                          <TableCell sx={actions_column}>
                            {!!property.deletedAt ? (
                              <Tooltip title="Restore Property">
                                <IconButton aria-label="restore" onClick={() => handleRestore(property.id)}>
                                  <SettingsBackupRestoreIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Fragment>
                                <Tooltip title="Edit Property">
                                  <IconButton aria-label="edit" onClick={() => onEditProperty(property)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Property">
                                  <IconButton aria-label="remove" onClick={() => handleRemove(property.id)}>
                                    <DeleteForeverIcon />
                                  </IconButton>
                                </Tooltip>
                              </Fragment>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                          <strong>No Data</strong>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <Box>
          {isSearching ? (
            <Spinner />
          ) : propertiesList.length ? (
            propertiesList.map((property) => (
              <MobilePropertyCard
                key={property.id}
                property={property}
                onEdit={onEditProperty}
                onRemove={handleRemove}
                onRestore={handleRestore}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No properties found
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Pagination - Desktop */}
      {!isMobile && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Properties per page"
        />
      )}

      {/* Pagination - Mobile */}
      {isMobile && totalCount > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: 2,
          mt: 3,
          mb: 2,
        }}>
          <Button 
            variant="outlined" 
            onClick={() => handleChangePage(null, page - 1)} 
            disabled={page === 0}
            size="small"
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => handleChangePage(null, page + 1)} 
            disabled={(page + 1) * rowsPerPage >= totalCount}
            size="small"
          >
            Next
          </Button>
        </Box>
      )}

      <PropertyDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        currentProperty={currentProperty}
        setCurrentProperty={setCurrentProperty}
        isNewProperty={isNewProperty}
        createProperty={createProperty}
        updateProperty={updateProperty}
        showInvalid={showInvalid}
      />

      {/* Duplicate Property Found Dialog */}
      <Dialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff3e0' }}>
          <Box sx={{ 
            bgcolor: '#ff9800', 
            borderRadius: '50%', 
            width: 32, 
            height: 32, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            ⚠️
          </Box>
          <Typography variant="h6">Property Already Exists</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              A property with this address already exists in the database:
            </Typography>
            
            {duplicateProperty && (
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1, 
                my: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  📍 {duplicateProperty.streetNumber} {duplicateProperty.streetName}
                  {duplicateProperty.unit && ` #${duplicateProperty.unit}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {duplicateProperty.city}, {duplicateProperty.state} {duplicateProperty.zipCode}
                </Typography>
              </Box>
            )}
            
            <Typography variant="body2" sx={{ mt: 2 }}>
              Would you like to view the existing property?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setShowDuplicateDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleGoToExistingProperty} 
            variant="contained" 
            color="primary"
          >
            Go to Existing Property
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Unit Building Dialog */}
      <Dialog
        open={showMultiUnitDialog}
        onClose={() => setShowMultiUnitDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e3f2fd' }}>
          <Box sx={{ 
            bgcolor: '#2196f3', 
            borderRadius: '50%', 
            width: 32, 
            height: 32, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'white',
            fontSize: '16px'
          }}>
            🏢
          </Box>
          <Typography variant="h6">Multi-Unit Building Detected</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              This address appears to be a <strong>multi-unit building</strong>.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Records with unit numbers already exist at this address:
            </Typography>
            
            {currentProperty && (
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1, 
                my: 2,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  📍 {currentProperty.streetNumber} {currentProperty.streetName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {currentProperty.city}, {currentProperty.state} {currentProperty.zipCode}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Existing units at this address:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {existingUnits.map((unit, idx) => (
                      <Chip 
                        key={idx}
                        size="small" 
                        label={`#${unit}`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Unit number is required.</strong> Please enter the unit/apartment number, then try saving again.
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setShowMultiUnitDialog(false)} 
            variant="contained" 
            color="primary"
          >
            Enter Unit Number
          </Button>
        </DialogActions>
      </Dialog>

      {/* Similar Addresses Found Dialog */}
      <Dialog
        open={showSimilarDialog}
        onClose={() => { setShowSimilarDialog(false); setPendingCreateData(null); setSimilarProperties([]); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff8e1' }}>
          <Box sx={{ 
            bgcolor: '#f9a825', 
            color: '#fff', 
            borderRadius: '50%', 
            width: 36, 
            height: 36, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 20,
          }}>
            ?
          </Box>
          <Typography variant="h6">Similar Addresses Found</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>
              No exact match, but these existing addresses look similar:
            </Typography>
            <Box sx={{ mt: 2 }}>
              {similarProperties.map((prop) => (
                <Box
                  key={prop.id}
                  onClick={() => handleGoToSimilarProperty(prop.id)}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1976d2' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {prop.address}
                  </Typography>
                  <Chip 
                    label={`${Math.round(prop.similarity * 100)}%`} 
                    size="small" 
                    color={prop.similarity > 0.7 ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Click on an address to open it, or create a new one if none of these match.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => { setShowSimilarDialog(false); setPendingCreateData(null); setSimilarProperties([]); }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={forceCreateProperty} 
            variant="contained" 
            color="warning"
          >
            Create New Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
